/**
 * Legacy client-facing order journey colors (My Orders timeline, Order Pipeline).
 * Staff Orders module list/detail uses `@/components/StatusBadge` with `domain="order"`.
 */

/** The colored Badge variants exposed by `components/ui/badge.tsx`. */
export type StatusBadgeVariant =
  | "info"
  | "success"
  | "warning"
  | "purple"
  | "teal"
  | "orange"
  | "cyan"
  | "indigo"
  | "destructive"
  | "secondary"
  | "default";

/** Order-level status → Badge variant. */
export const getOrderStatusVariant = (
  status: string | null | undefined,
): StatusBadgeVariant => {
  switch (status) {
    case "draft":
      return "secondary";
    case "live_store":
      return "info";
    case "needs_approval":
    case "moq_review":
    case "on_hold":
      return "warning";
    case "pending_pricing_review":
    case "live_store_item":
    case "approved_for_payment":
      return "orange";
    case "delivered":
      return "success";
    case "in_production":
    case "shipping":
    case "shipped":
      return "indigo";
    case "failed_store":
    case "failed_store_item":
    case "cancelled":
      return "destructive";
    case "Order Placed":
      return "info";
    case "Payment Pending":
      return "warning";
    case "Payment Processing":
      return "orange";
    case "Paid":
      return "success";
    case "Sent to Supplier":
      return "indigo";
    case "Order Ready To Ship":
      /** Almost-out-the-door — a brighter cyan than the indigo "with supplier". */
      return "cyan";
    case "Sample sent for client approval":
      return "cyan";
    case "Sample Revision Requested":
      return "warning";
    case "Sample Approved":
      return "teal";
    case "In Production":
      return "indigo";
    case "Ongoing full production":
      return "purple";
    case "Shipped":
      return "cyan";
    case "Delivered":
      return "success";
    case "Won":
      return "success";
    default:
      return "secondary";
  }
};

/** Per-product production status → Badge variant. */
export const getProductionStatusVariant = (
  status: string | null | undefined,
): StatusBadgeVariant => {
  switch (status) {
    case "Pending":
      /** Amber reads as "queued / awaiting start" — never flat gray. */
      return "warning";
    case "Sample sent for client approval":
      return "cyan";
    case "Sample Revision Requested":
      return "warning";
    case "Sample Approved":
      return "teal";
    case "In Production":
      return "indigo";
    case "Ongoing full production":
      return "purple";
    case "Completed":
      return "success";
    default:
      return "secondary";
  }
};

/** Payment status → Badge variant. */
export const getPaymentStatusVariant = (
  status: string | null | undefined,
): StatusBadgeVariant => {
  switch (status) {
    case "Unpaid":
      return "secondary";
    case "Payment Pending":
      return "warning";
    case "Payment Processing":
      return "orange";
    case "Partially Paid":
      return "cyan";
    case "Paid":
      return "success";
    default:
      return "secondary";
  }
};

/**
 * Soft tone classes per variant — semantic tokens only (no raw palette / dark: pairs).
 * Shared by `StatusPill`, `StatusSelect`, and order pipeline surfaces.
 * @deprecated Migrate consumers to `@/components/StatusBadge` + `design-system/status`.
 */
const STATUS_TONES: Record<
  StatusBadgeVariant,
  { surface: string; dot: string }
> = {
  success: {
    surface: "bg-success/10 text-success border border-success/20",
    dot: "bg-success",
  },
  warning: {
    surface: "bg-warning-background text-warning border border-warning-border",
    dot: "bg-warning",
  },
  info: {
    surface: "bg-info-background text-info border border-info-border",
    dot: "bg-info",
  },
  indigo: {
    surface: "bg-primary/10 text-primary border border-primary/20",
    dot: "bg-primary",
  },
  teal: {
    surface: "bg-info-background text-info border border-info-border",
    dot: "bg-info",
  },
  cyan: {
    surface: "bg-info-background text-info border border-info-border",
    dot: "bg-info",
  },
  orange: {
    surface: "bg-warning-background text-warning border border-warning-border",
    dot: "bg-warning",
  },
  purple: {
    surface: "bg-primary/10 text-primary border border-primary/20",
    dot: "bg-primary",
  },
  destructive: {
    surface: "bg-destructive-background text-destructive border border-destructive-border",
    dot: "bg-destructive",
  },
  secondary: {
    surface: "bg-muted text-muted-foreground border border-border",
    dot: "bg-muted-foreground",
  },
  default: {
    surface: "bg-muted text-muted-foreground border border-border",
    dot: "bg-muted-foreground",
  },
};

/** Tinted surface classes (text + bg) for a status variant. */
export const statusSurfaceClass = (variant: StatusBadgeVariant): string =>
  (STATUS_TONES[variant] ?? STATUS_TONES.secondary).surface;

/** Leading-dot fill class for a status variant. */
export const statusDotClass = (variant: StatusBadgeVariant): string =>
  (STATUS_TONES[variant] ?? STATUS_TONES.secondary).dot;

/**
 * The ordered, client-facing order journey — what a buyer can expect their
 * order to pass through, start to finish. Rendered as a stepper on the client
 * My Orders page so the customer sees the whole road ("what to expect") even
 * before the order has reached a given milestone. This is intentionally a
 * SUPERSET of the raw statuses an order may carry: some staff/legacy statuses
 * (e.g. "In Production") are synonyms that map onto a step rather than adding
 * one — see `clientTimelineStepIndex`.
 */
export const CLIENT_ORDER_TIMELINE = [
  "Order Placed",
  "Payment Pending",
  "Payment Processing",
  "Paid",
  "Sent to Supplier",
  "Order Ready To Ship",
  "Order Shipped",
  "Delivered",
] as const;

export type ClientTimelineStep = (typeof CLIENT_ORDER_TIMELINE)[number];

/** Step index constants — keep the synonym mapping below readable. */
const STEP = {
  placed: 0,
  paymentPending: 1,
  paymentProcessing: 2,
  paid: 3,
  sentToSupplier: 4,
  readyToShip: 5,
  shipped: 6,
  delivered: 7,
} as const;

/**
 * Map an order's free-text status onto an index in `CLIENT_ORDER_TIMELINE`.
 *
 * Pure + case-insensitive, and tolerant of synonyms / legacy Zoho strings:
 *  - "Shipped" / "Out for delivery" → "Order Shipped"
 *  - "In Production" / "Ongoing full production" / "samples …" → just AFTER
 *    "Sent to Supplier" (the supplier is actively working it), i.e. mapped to
 *    "Sent to Supplier" so the timeline shows the order has reached the maker.
 *  - "Won" / "Completed" / "Delivered" → the final step.
 *
 * Returns the matched step index, or 0 (Order Placed) for an unknown / empty
 * status so the stepper always has a sensible "current" position to anchor on.
 */
export const clientTimelineStepIndex = (
  rawStatus: string | null | undefined,
): number => {
  const s = (rawStatus ?? "").trim().toLowerCase();
  if (!s) return STEP.placed;

  /** Final state — check first so "delivered" never matches an earlier rule. */
  if (
    s.includes("deliver") ||
    s.includes("won") ||
    s === "completed" ||
    s === "complete"
  )
    return STEP.delivered;

  /**
   * More specific "ready to ship" must beat the generic "ship" rule below
   * (the former contains the latter as a substring).
   */
  if (s.includes("ready to ship")) return STEP.readyToShip;
  if (s.includes("ship")) return STEP.shipped; /** "Shipped" / "Order Shipped" */

  /** Supplier is actively producing — treat as "reached the supplier". */
  if (
    s.includes("production") ||
    s.includes("sample") ||
    s.includes("supplier")
  )
    return STEP.sentToSupplier;

  if (s.includes("paid")) return STEP.paid;
  if (s.includes("payment processing") || s.includes("processing"))
    return STEP.paymentProcessing;
  if (s.includes("payment pending") || s.includes("pending"))
    return STEP.paymentPending;

  if (s.includes("placed") || s.includes("submitted") || s.includes("new"))
    return STEP.placed;

  return STEP.placed;
};
